import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { FileCheck2, Gavel, ShieldOff } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Input, NativeSelect, SourceText, Textarea, useSave } from "@ncp/ui";

interface ComplianceReport {
  id: string; statement: string; submittedAt: string; submittedBy: string; documents: Array<{ id: string; name: string }>;
  review?: { outcome: "Accepted" | "Insufficient"; note: string; at: string; by: string } | null;
}
/** What an entry allows the signed-in officer now, and what its journal holds beyond the alert (scenario 6.44). */
export interface CommitmentLife {
  id: string; allowedStatuses: string[]; mayWaive: boolean; mayOpenEnforcement: boolean; mayReview: boolean; reports: ComplianceReport[];
  waiver?: { reasons: string; legalBasis: string; legalBasisAr?: string | null; by: string; at: string } | null;
  enforcement?: { caseId: string; caseNumber: string; reasons: string; by: string; at: string } | null;
}

export function useCommitmentLifecycle(caseId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["analysis", "commitments", caseId, "lifecycle"], queryFn: () => api.get<CommitmentLife[]>(`/api/analysis/cases/${caseId}/commitments/lifecycle`) });
}

/** A step a breach or a reopening takes: it is recorded with the officer's reasons. */
const needsReasons = (from: string, to: string) => to === "Breached" || from === "Met" || from === "Breached";

/**
 * The officer's steps on one entry of the register after the decision (scenario 6.44): the statuses the officer may record now (a breach and a
 * reopening with reasons), the review of each compliance report the undertaking filed on the portal, the waiver with reasons and the legal basis,
 * and the enforcement file a breach opens.
 */
export function CommitmentSteps({ caseId, sequence, status, life, statusSteps = true }: { caseId: string; sequence: number; status: string; life?: CommitmentLife; statusSteps?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const enumLabel = useEnumLabel();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["analysis", "commitments", caseId] }); };
  const id = life?.id ?? "";
  const [step, setStep] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const setStatus = useSave({
    mutationFn: (v: { to: string; note?: string }) => api.post(`/api/analysis/commitments/${id}/status`, { status: v.to, note: v.note ?? null }),
    saved: t("backoffice:analysis.commitmentUpdated"),
    onSuccess: () => { setStep(null); setNote(""); refresh(); },
  });
  const [waiving, setWaiving] = useState(false);
  const [waiver, setWaiver] = useState({ reasons: "", legalBasis: "", legalBasisAr: "" });
  const waive = useSave({
    mutationFn: () => api.post(`/api/analysis/commitments/${id}/waive`, { reasons: waiver.reasons.trim(), legalBasis: waiver.legalBasis.trim(), legalBasisAr: waiver.legalBasisAr.trim() || null }),
    saved: t("backoffice:caseRecords.commitmentLifecycle.waived"),
    onSuccess: () => { setWaiving(false); setWaiver({ reasons: "", legalBasis: "", legalBasisAr: "" }); refresh(); },
  });
  const [enforcing, setEnforcing] = useState(false);
  const [why, setWhy] = useState("");
  const enforce = useSave({
    mutationFn: () => api.post<{ caseId: string; caseNumber: string }>(`/api/analysis/commitments/${id}/enforcement`, { reasons: why.trim() }),
    expect: "record",
    saved: (r) => t("backoffice:caseRecords.commitmentLifecycle.enforcementOpened", { number: r.caseNumber }),
    onSuccess: () => { setEnforcing(false); setWhy(""); refresh(); },
  });
  const [reviewing, setReviewing] = useState<ComplianceReport | null>(null);
  const [review, setReview] = useState({ outcome: "Accepted", note: "" });
  const record = useSave({
    mutationFn: () => api.post(`/api/analysis/commitments/${id}/compliance-reports/${reviewing!.id}/review`, { outcome: review.outcome, note: review.note.trim() }),
    saved: t("backoffice:caseRecords.commitmentLifecycle.reviewed"),
    onSuccess: () => { setReviewing(null); setReview({ outcome: "Accepted", note: "" }); refresh(); },
  });
  if (!life) return null;
  const draft = (k: string) => `case:${caseId}:commitment:${id}:${k}`;

  return (
    <>
      {life.reports.length > 0 ? (
        <div className="mt-2 rounded-xl border border-sand-200 bg-sand-50/60 p-3 text-xs">
          <p className="font-medium text-sand-700">{t("backoffice:caseRecords.commitmentLifecycle.reports")}</p>
          <ul className="mt-1 space-y-2">
            {life.reports.map((r) => (
              <li key={r.id}>
                <p className="text-muted-foreground">{formatDate(r.submittedAt, locale)} · <SourceText text={r.submittedBy} /></p>
                <p className="text-aeblack-900/85"><SourceText text={r.statement} /></p>
                <p className="text-muted-foreground">{t("backoffice:caseRecords.commitmentLifecycle.evidence")}: {r.documents.map((d, i) => <span key={d.id}>{i > 0 ? (ar ? "، " : ", ") : null}<SourceText text={d.name} /></span>)}</p>
                {r.review ? (
                  <p className="mt-0.5"><Badge size="sm" variant={r.review.outcome === "Accepted" ? "success" : "warning"}>{t(`backoffice:caseRecords.commitmentLifecycle.outcomes.${r.review.outcome}`)}</Badge> <SourceText text={r.review.by} /> · {formatDate(r.review.at, locale)} — <SourceText text={r.review.note} /></p>
                ) : life.mayReview ? (
                  <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => { setReviewing(r); setReview({ outcome: "Accepted", note: "" }); }}><FileCheck2 /> {t("backoffice:caseRecords.commitmentLifecycle.review")}</Button>
                ) : <Badge size="sm" variant="outline" className="mt-1">{t("backoffice:caseRecords.commitmentLifecycle.awaitingReview")}</Badge>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {life.waiver ? (
        <p className="mt-2 rounded-lg bg-sand-50 p-2 text-xs">
          <span className="font-medium">{t("backoffice:caseRecords.commitmentLifecycle.waivedBy", { by: life.waiver.by, date: formatDate(life.waiver.at, locale) })}</span>{" "}
          <SourceText text={life.waiver.reasons} /> · {t("backoffice:caseRecords.commitmentLifecycle.legalBasis")}: <SourceText text={ar && life.waiver.legalBasisAr ? life.waiver.legalBasisAr : life.waiver.legalBasis} />
        </p>
      ) : null}
      {life.enforcement ? (
        <p className="mt-2 text-xs">
          <Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => navigate(`/cases/${life.enforcement!.caseId}`)}><Gavel /> {t("backoffice:caseRecords.commitmentLifecycle.enforcementFile", { number: life.enforcement.caseNumber })}</Button>
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2">
        {(statusSteps ? life.allowedStatuses : []).map((s) => (
          <Button key={s} size="sm" variant="ghost" loading={setStatus.isPending && setStatus.variables?.to === s}
            onClick={() => { if (needsReasons(status, s)) { setStep(s); setNote(""); } else setStatus.mutate({ to: s }); }}>
            {enumLabel("commitmentStatus", s)}
          </Button>
        ))}
        {life.mayWaive ? <Button size="sm" variant="ghost" onClick={() => setWaiving(true)}><ShieldOff /> {t("backoffice:caseRecords.commitmentLifecycle.waive")}</Button> : null}
        {life.mayOpenEnforcement ? <Button size="sm" variant="outline" onClick={() => { setEnforcing(true); setWhy(""); }}><Gavel /> {t("backoffice:caseRecords.commitmentLifecycle.openEnforcement")}</Button> : null}
      </div>

      <Dialog open={step !== null} onOpenChange={(o) => { if (!o) setStep(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("backoffice:caseRecords.commitmentLifecycle.stepTitle", { n: sequence, status: step ? enumLabel("commitmentStatus", step) : "" })}</DialogTitle>
            <DialogDescription>{t("backoffice:caseRecords.commitmentLifecycle.stepHint")}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:caseRecords.commitmentAlerts.reason")} required>{(fid) => <Textarea id={fid} rows={4} value={note} onChange={(e) => setNote(e.target.value)} dir="auto" draft={step ? draft(`status:${step}`) : null} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStep(null)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={note.trim().length < 20} loading={setStatus.isPending} onClick={() => step && setStatus.mutate({ to: step, note: note.trim() })}>{t("actions.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={waiving} onOpenChange={setWaiving}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("backoffice:caseRecords.commitmentLifecycle.waiveTitle", { n: sequence })}</DialogTitle>
            <DialogDescription>{t("backoffice:caseRecords.commitmentLifecycle.waiveHint")}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:caseRecords.commitmentAlerts.reason")} required>{(fid) => <Textarea id={fid} rows={4} value={waiver.reasons} onChange={(e) => setWaiver((w) => ({ ...w, reasons: e.target.value }))} dir="auto" draft={draft("waiver:reasons")} />}</Field>
          <Field label={t("backoffice:caseRecords.commitmentLifecycle.legalBasis")} hint={t("backoffice:caseRecords.commitmentLifecycle.legalBasisHint")} required>{(fid) => <Input id={fid} value={waiver.legalBasis} onChange={(e) => setWaiver((w) => ({ ...w, legalBasis: e.target.value }))} dir="auto" />}</Field>
          <Field label={t("backoffice:caseRecords.commitmentLifecycle.legalBasisAr")}>{(fid) => <Input id={fid} value={waiver.legalBasisAr} onChange={(e) => setWaiver((w) => ({ ...w, legalBasisAr: e.target.value }))} dir="rtl" lang="ar" />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWaiving(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={waiver.reasons.trim().length < 20 || waiver.legalBasis.trim().length < 10} loading={waive.isPending} onClick={() => waive.mutate()}><ShieldOff /> {t("backoffice:caseRecords.commitmentLifecycle.waive")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={enforcing} onOpenChange={setEnforcing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("backoffice:caseRecords.commitmentLifecycle.enforcementTitle", { n: sequence })}</DialogTitle>
            <DialogDescription>{t("backoffice:caseRecords.commitmentLifecycle.enforcementHint")}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:r3Process.signals.reasons")} required>{(fid) => <Textarea id={fid} rows={4} value={why} onChange={(e) => setWhy(e.target.value)} dir="auto" draft={draft("enforcement:reasons")} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEnforcing(false)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={why.trim().length < 20} loading={enforce.isPending} onClick={() => enforce.mutate()}><Gavel /> {t("backoffice:r3Process.signals.decide")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewing !== null} onOpenChange={(o) => { if (!o) setReviewing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("backoffice:caseRecords.commitmentLifecycle.reviewTitle", { n: sequence })}</DialogTitle>
            <DialogDescription>{reviewing ? <SourceText text={reviewing.statement} /> : null}</DialogDescription>
          </DialogHeader>
          <Field label={t("backoffice:caseRecords.commitmentLifecycle.outcome")}>{(fid) => (
            <NativeSelect id={fid} value={review.outcome} onChange={(e) => setReview((r) => ({ ...r, outcome: e.target.value }))}>
              {["Accepted", "Insufficient"].map((o) => <option key={o} value={o}>{t(`backoffice:caseRecords.commitmentLifecycle.outcomes.${o}`)}</option>)}
            </NativeSelect>
          )}</Field>
          <Field label={t("backoffice:caseRecords.commitmentLifecycle.reviewNote")} required>{(fid) => <Textarea id={fid} rows={4} value={review.note} onChange={(e) => setReview((r) => ({ ...r, note: e.target.value }))} dir="auto" draft={reviewing ? draft(`review:${reviewing.id}`) : null} />}</Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewing(null)}>{t("actions.cancel")}</Button>
            <Button variant="gold" disabled={review.note.trim().length < 20} loading={record.isPending} onClick={() => record.mutate()}><FileCheck2 /> {t("backoffice:caseRecords.commitmentLifecycle.recordReview")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
